import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Search, FileText, CheckCircle, XCircle, Clock, Printer, 
  DollarSign, Calendar, RefreshCw, PlusCircle, MessageCircle, Edit2, X
} from 'lucide-react';

const Billing = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sendingId, setSendingId] = useState(null); // Track which invoice is being sent
  
  // Edit Modal State
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', is_paid: false });

  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      // Fetch all invoices by following pagination links
      let allInvoices = [];
      let nextUrl = '/invoices/?page_size=500';
      while (nextUrl) {
        const res = await api.get(nextUrl);
        const data = res.data;
        allInvoices = allInvoices.concat(data.results || []);
        // next is a full URL — extract just the path+query after /api/v1
        nextUrl = data.next ? data.next.replace(/^https?:\/\/[^/]+\/api\/v1/, '') : null;
      }
      setInvoices(allInvoices);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoices = async () => {
    if (!window.confirm("Are you sure you want to generate invoices for all active customers for the current month?")) return;
    setGenerating(true);
    try {
      const res = await api.post('/invoices/generate_monthly/');
      alert(res.data.message);
      fetchInvoices();
    } catch (err) {
      alert("Failed to generate invoices.");
    } finally {
      setGenerating(false);
    }
  };

  // 🔥 Send WhatsApp Receipt
  const handleSendReceipt = async (id) => {
    if (!window.confirm("Send WhatsApp receipt to this customer?")) return;
    setSendingId(id);
    try {
      const res = await api.post(`/invoices/${id}/send_receipt/`);
      alert(res.data.status || "Message Sent!");
    } catch (err) {
      alert(err.response?.data?.error || "Failed to send message. Check if customer has a phone number.");
    } finally {
      setSendingId(null);
    }
  };

  // 🔥 Open Edit Modal
  const openEditModal = (inv) => {
    setEditingInvoice(inv);
    setEditForm({
      amount: inv.amount,
      is_paid: inv.is_paid
    });
  };

  // 🔥 Save Edited Invoice
  const saveEdit = async () => {
    try {
      await api.patch(`/invoices/${editingInvoice.id}/update_invoice/`, editForm);
      alert("Invoice updated!");
      setEditingInvoice(null);
      fetchInvoices();
    } catch (err) {
      alert("Failed to update invoice");
    }
  };

  const handlePrint = (invoice) => {
    // ... (Your existing print logic remains same) ...
    const statusColor = invoice.is_paid ? '#166534' : '#991b1b';
    const statusText = invoice.is_paid ? 'PAID' : 'PENDING PAYMENT';
    const statusBg = invoice.is_paid ? '#dcfce7' : '#fee2e2';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice #${invoice.id}</title>
          <style>
             /* ... (Keep your existing CSS styles here) ... */
             body { font-family: 'Inter', sans-serif; padding: 40px; color: #1f2937; background: #fff; }
             .header { display: flex; justify-content: space-between; margin-bottom: 40px; border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; }
             .company-info h1 { margin: 0; font-size: 28px; color: #2563eb; }
             .invoice-meta { text-align: right; }
             .status-badge { display: inline-block; padding: 6px 12px; border-radius: 6px; font-weight: bold; background: ${statusBg}; color: ${statusColor}; }
             table { width: 100%; border-collapse: collapse; margin-top: 20px; }
             th, td { padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: left; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-info">
              <h1>OpticFlow ISP</h1>
              <p>support@opticflow.com | +92 304 3330360</p>
            </div>
            <div class="invoice-meta">
              <h2>INVOICE #${invoice.id.toString().padStart(4, '0')}</h2>
              <p>Date: ${new Date(invoice.created_at).toLocaleDateString()}</p>
              <div class="status-badge">${statusText}</div>
            </div>
          </div>
          <div style="margin-bottom: 20px;">
            <strong>Bill To:</strong> ${invoice.customer_name} (${invoice.customer_username})<br/>
            <strong>Package:</strong> ${invoice.package_name}
          </div>
          <table>
            <thead><tr><th>Description</th><th>Amount</th></tr></thead>
            <tbody>
              <tr><td>Monthly Subscription</td><td>PKR ${parseFloat(invoice.amount).toLocaleString()}</td></tr>
            </tbody>
          </table>
          <div style="text-align: right; margin-top: 20px; font-size: 20px; font-weight: bold;">
            Total: PKR ${parseFloat(invoice.amount).toLocaleString()}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = 
      inv.customer_username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'paid') return matchesSearch && inv.is_paid;
    if (filter === 'unpaid') return matchesSearch && !inv.is_paid;
    return matchesSearch;
  });

  const stats = {
    total: invoices.length,
    paid: invoices.filter(i => i.is_paid).length,
    partial: invoices.filter(i => !i.is_paid && parseFloat(i.paid_amount) > 0).length,
    unpaid: invoices.filter(i => !i.is_paid && parseFloat(i.paid_amount) === 0).length,
    revenue: invoices.filter(i => i.is_paid).reduce((sum, i) => sum + parseFloat(i.amount), 0)
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen relative">
      
      {/* 🔥 Edit Modal */}
      {editingInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold">Edit Invoice #{editingInvoice.id}</h3>
              <button onClick={() => setEditingInvoice(null)}><X size={20}/></button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (PKR)</label>
                <input 
                  type="number" 
                  value={editForm.amount}
                  onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                  className="w-full p-2 border rounded-lg"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={editForm.is_paid}
                  onChange={(e) => setEditForm({...editForm, is_paid: e.target.checked})}
                  className="w-5 h-5 text-blue-600"
                />
                <span className="text-sm font-medium">Mark as Paid?</span>
              </div>

              <div className="flex gap-3 mt-6">
                <button 
                  onClick={() => setEditingInvoice(null)}
                  className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button 
                  onClick={saveEdit}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header & Stats */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
            <span className="w-1 h-6 bg-blue-600 rounded-full"></span> Billing & Invoices
          </h2>
          <p className="text-sm text-gray-500">Manage customer invoices and payments</p>
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={handleGenerateInvoices}
            disabled={generating}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
              generating ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
            }`}
          >
            {generating ? <RefreshCw size={18} className="animate-spin" /> : <PlusCircle size={18} />}
            {generating ? 'Generating...' : 'Generate Monthly'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatBadge label="Total Invoices" count={stats.total} icon={FileText} color="bg-gray-100 text-gray-700" />
        <StatBadge label="Paid" count={stats.paid} icon={CheckCircle} color="bg-green-100 text-green-700" />
        <StatBadge label="Partial" count={stats.partial} icon={Clock} color="bg-orange-100 text-orange-700" />
        <StatBadge label="Unpaid" count={stats.unpaid} icon={Clock} color="bg-red-100 text-red-700" />
        <StatBadge label="Revenue" count={`PKR ${stats.revenue.toLocaleString()}`} icon={DollarSign} color="bg-blue-100 text-blue-700" />
      </div>

      {/* Controls */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex gap-2 w-full md:w-auto">
          {['all', 'paid', 'unpaid'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
                filter === f ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
          <input 
            type="text" 
            placeholder="Search customer..." 
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-semibold">
              <tr>
                <th className="p-4">Invoice #</th>
                <th className="p-4">Customer</th>
                <th className="p-4">Package</th>
                <th className="p-4">Month</th>
                <th className="p-4">Amount</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center">Loading...</td></tr>
              ) : filteredInvoices.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-gray-500">No invoices found.</td></tr>
              ) : (
                filteredInvoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-blue-50/30 transition-colors">
                    <td className="p-4 font-mono text-sm text-gray-500">#{inv.id}</td>
                    <td className="p-4">
                      <div className="font-bold text-gray-900">{inv.customer_username}</div>
                      <div className="text-xs text-gray-500">{inv.customer_name}</div>
                    </td>
                    <td className="p-4 text-sm text-gray-600">{inv.package_name}</td>
                    <td className="p-4 text-sm text-gray-600">
                      {new Date(inv.month).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-gray-900">PKR {parseFloat(inv.amount).toLocaleString()}</div>
                      {parseFloat(inv.previous_balance) > 0 && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          Monthly: PKR {(parseFloat(inv.amount) - parseFloat(inv.previous_balance)).toLocaleString()}
                          {' + '}Prev due: PKR {parseFloat(inv.previous_balance).toLocaleString()}
                        </div>
                      )}
                      {!inv.is_paid && parseFloat(inv.paid_amount) > 0 && (
                        <div className="mt-1">
                          <div className="text-xs text-orange-600 font-medium">
                            Paid: PKR {parseFloat(inv.paid_amount).toLocaleString()} / Remaining: PKR {(parseFloat(inv.amount) - parseFloat(inv.paid_amount)).toLocaleString()}
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                            <div
                              className="bg-orange-400 h-1 rounded-full"
                              style={{ width: `${Math.min(100, (parseFloat(inv.paid_amount) / parseFloat(inv.amount)) * 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      {inv.is_paid ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                          <CheckCircle size={12}/> Paid
                        </span>
                      ) : parseFloat(inv.paid_amount) > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700">
                          <Clock size={12}/> Partial
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
                          <Clock size={12}/> Unpaid
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right flex justify-end gap-2">
                      
                      {/* 🔥 Send WhatsApp Button */}
                      {inv.is_paid && (
                        <button 
                          onClick={() => handleSendReceipt(inv.id)}
                          disabled={sendingId === inv.id}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Send WhatsApp Receipt"
                        >
                          {sendingId === inv.id ? <RefreshCw size={18} className="animate-spin"/> : <MessageCircle size={18} />}
                        </button>
                      )}

                      {/* 🔥 Edit Button */}
                      <button 
                        onClick={() => openEditModal(inv)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit Invoice"
                      >
                        <Edit2 size={18} />
                      </button>

                      {/* Print Button */}
                      <button 
                        onClick={() => handlePrint(inv)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Print Invoice"
                      >
                        <Printer size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Helper Component
const StatBadge = ({ label, count, icon: Icon, color }) => (
  <div className={`px-3 py-1.5 rounded-lg border text-xs font-bold flex items-center gap-2 ${color}`}>
    <Icon size={14} /> {label}: <span className="text-sm">{count}</span>
  </div>
);

export default Billing;

